import { useState } from 'react'
import Lightbox from '../Lightbox'

// Photo gallery for a trek. Reads trek.gallery — [{ src, alt, caption }].
// Uses a CSS column masonry so portrait and landscape photos both keep their
// natural aspect ratio — nothing is cropped to a fixed shape.
// Click any photo to open the shared fullscreen Lightbox (prev/next, zoom, rotate).
const MAX_VISIBLE = 8   // show this many in the grid; the rest are reachable via the lightbox

export default function TrekPhotos({ trek }) {
  const [lightbox, setLightbox] = useState(null)
  const photos = (trek?.gallery || []).filter(p => p.src && !p.src.startsWith('TODO'))

  if (!photos.length) return null

  const url = src => `${process.env.PUBLIC_URL}${src}`
  const visible = photos.slice(0, MAX_VISIBLE)
  const remaining = photos.length - visible.length

  return (
    <div style={{ marginBottom: '60px' }}>
      <h2 className="section-title" style={{ fontSize: '24px', marginBottom: '24px' }}>Trail gallery.</h2>

      <div className="gallery-masonry">
        {visible.map((p, i) => {
          const isLast = i === visible.length - 1
          const showMore = isLast && remaining > 0
          return (
            <button
              key={p.src}
              onClick={() => setLightbox(i)}
              aria-label={showMore ? `View all ${photos.length} photos` : `View enlarged: ${p.alt}`}
              className="gallery-item"
            >
              <div style={{ position: 'relative' }}>
                <img
                  src={url(p.src)}
                  alt={p.alt}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: 'auto',           // natural aspect ratio — never squashed or cropped
                    borderRadius: 'var(--radius)',
                    display: 'block',
                    transition: 'var(--transition)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.88' }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1' }}
                />
                {showMore && (
                  // "+N more" overlay on the last visible tile — opens the lightbox,
                  // from which every photo is reachable via the arrows.
                  <div style={{
                    position: 'absolute', inset: 0, borderRadius: 'var(--radius)',
                    background: 'rgba(26,28,27,0.62)', color: 'white',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'var(--font-heading)',
                  }}>
                    <span style={{ fontSize: '24px', fontWeight: 700, lineHeight: 1 }}>+{remaining}</span>
                    <span className="data" style={{ fontSize: '11px', marginTop: '4px', letterSpacing: '0.5px' }}>MORE PHOTOS</span>
                  </div>
                )}
              </div>
              {!showMore && p.caption && !p.caption.startsWith('TODO') && (
                <div style={{ fontSize: '12px', color: 'var(--ink-light)', marginTop: '8px' }}>
                  {p.caption}
                </div>
              )}
            </button>
          )
        })}
      </div>

      {lightbox != null && (
        <Lightbox
          images={photos.map(p => url(p.src))}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onNavigate={setLightbox}
        />
      )}

      <style>{`
        .gallery-masonry {
          column-count: 4;
          column-gap: 16px;
        }
        .gallery-item {
          break-inside: avoid;
          -webkit-column-break-inside: avoid;
          page-break-inside: avoid;
          display: block;
          width: 100%;
          margin: 0 0 16px;
          padding: 0;
          border: none;
          background: none;
          cursor: pointer;
          text-align: left;
        }
        @media (max-width: 1100px) { .gallery-masonry { column-count: 3; } }
        @media (max-width: 760px)  { .gallery-masonry { column-count: 2; } }
        @media (max-width: 460px)  { .gallery-masonry { column-count: 1; } }
      `}</style>
    </div>
  )
}
